const APP_NAME = process.env.APP_NAME || 'terrace'
const APP_ENV = process.env.NEXT_PUBLIC_APP_ENV
const APP_IS_PROD = APP_ENV === 'prod'
const APP_IS_LOCAL = APP_ENV === 'local'
const APP_IS_PREVIEW = (process.env.VERCEL_ENV || process.env.NEXT_PUBLIC_VERCEL_ENV) === 'preview'
const COMMIT_SHA = process.env.VERCEL_GIT_COMMIT_SHA || process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA || ''
const APP_REV = `${COMMIT_SHA}`.slice(0, 8)
const GQL_URI = process.env.NEXT_PUBLIC_UX_GQL_URI
const GQL_PUBLIC_URI = process.env.NEXT_PUBLIC_UX_GQL_PUBLIC_URI

// DEPLOYMENT
const DEPLOYED_URL =
  process.env.VERCEL_BRANCH_URL ||
  process.env.NEXT_PUBLIC_VERCEL_BRANCH_URL ||
  process.env.VERCEL_URL ||
  process.env.NEXT_PUBLIC_VERCEL_URL
const APP_PREVIEW_BASE_URL = APP_IS_PREVIEW && DEPLOYED_URL ? `https://${DEPLOYED_URL}` : 'http://localhost:5011'

// AUTH
const AUTH0_AUDIENCE = process.env.AUTH0_AUDIENCE
// This is needed to support the nextjs-auth0 sdk. It should only be set for
// prod env and main branch for dev. All other deployments are Vercel URLs.
// It is also used below for APP_BASE_URL.
process.env.APP_BASE_URL = process.env.APP_BASE_URL || process.env.NEXT_PUBLIC_APP_BASE_URL || APP_PREVIEW_BASE_URL

const authApiPaths = {
  LOGIN: '/auth/login',
  LOGOUT: '/auth/logout',
  CALLBACK: '/auth/callback',
}

const UNIVERSAL_LEAD_COOKIE = 'knock_universal_lead'
const COOKIE_YES_CONSENT_COOKIE = 'cookieyes-consent'
const KNOCK_PRIVACY_COOKIE = 'knock_privacy'
// Set on every successful Auth0 callback. Marks the browser as one that could
// still hold an Auth0 tenant session, which is what makes the silent SSO probe
// on /auth/signup worth running. See @phoenix-web/auth sso-probe.
const KNOCK_RETURNING_USER_COOKIE = 'knock_returning_user'
// Set when a probe came back with no Auth0 session, so we stop asking.
const KNOCK_SSO_PROBED_COOKIE = 'knock_sso_probed'

const APP_BASE_URL = process.env.NEXT_PUBLIC_APP_BASE_URL || APP_PREVIEW_BASE_URL
const GQL_PROXY_URI = `${APP_BASE_URL}/api/graphql`

const AUTH0_ISSUER_BASE_URL = process.env.AUTH0_ISSUER_BASE_URL
const PUBLIC_AUTH0_ISSUER_BASE_URL = process.env.NEXT_PUBLIC_AUTH0_ISSUER_BASE_URL
const PUBLIC_AUTH0_CLIENT_ID = process.env.NEXT_PUBLIC_AUTH0_CLIENT_ID

// THIRDPARTY KEYS
const DD_CLIENT_TOKEN = process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN
const DD_APPLICATION_ID = process.env.NEXT_PUBLIC_DD_APPLICATION_ID
const DISABLE_ALL_ANALYTICS = process.env.NEXT_PUBLIC_DISABLE_ALL_ANALYTICS === 'true'
const DD_ENABLED = DD_CLIENT_TOKEN && DD_APPLICATION_ID && !DISABLE_ALL_ANALYTICS
const HEAP_TOKEN = process.env.NEXT_PUBLIC_HEAP_TOKEN
const HEAP_PROXY = process.env.NEXT_PUBLIC_HEAP_PROXY === 'true'
const GTAG_ID = process.env.NEXT_PUBLIC_GTAG_ID
const APPZI_TOKEN = process.env.NEXT_PUBLIC_APPZI_TOKEN
const COOKIE_YES_SCRIPT_ID = process.env.NEXT_PUBLIC_COOKIE_YES_SCRIPT_ID
const STORYBLOK_CDN_TOKEN = process.env.STORYBLOK_CDN_TOKEN

enum VALID_STORYBLOK_VERSIONS {
  draft = 'draft',
  published = 'published',
}

const STORYBLOK_VERSION = process.env.STORYBLOK_VERSION as VALID_STORYBLOK_VERSIONS
const LIVE_WEBINAR_SLUG = 'live-webinars'

// FRONTYARD URLS
const FRONTYARD_URL = process.env.NEXT_PUBLIC_FRONTYARD_URL
const QUALIFY_URL = process.env.NEXT_PUBLIC_QUALIFY_URL
const COURTYARD_URL = process.env.NEXT_PUBLIC_COURTYARD_URL
const HOMEBUYER_SIGNUP_URL = `${COURTYARD_URL}/auth/signup?audience=homebuyer`
const FAQ_URL = `${FRONTYARD_URL}/faq`
const RESOURCE_CENTER = `${FRONTYARD_URL}/resources`
const LIVE_WEBINARS_URL = `${RESOURCE_CENTER}/category/live-webinars`
const TERMS_OF_USE_URL = `${FRONTYARD_URL}/terms-of-use`
const CONTACT_US_URL = `${FRONTYARD_URL}/contact`
const PRIVACY_POLICY_URL = `${FRONTYARD_URL}/privacy-policy`
const COOKIE_POLICY_URL = `${FRONTYARD_URL}/cookie-policy`
const DO_NOT_SELL_INFO_URL = `${FRONTYARD_URL}/privacy-policy#notice-for-california-residents`

// TERRACE URLS
const TERRACE_AUTH_LOGIN_PATH = '/login'
const TERRACE_AUTH_SIGNUP_PATH = '/auth/signup'
const TERRACE_AUTH_SIGNUP_STEP2_PATH = '/auth/signup-step2'
const TERRACE_AUTH_LOGOUT_PATH = '/logout'
const TERRACE_EMAIL_VERIFY_PATH = '/auth/verify'
const TERRACE_HOME_DASHBOARD_PATH = '/dashboard'
const TERRACE_AGENT_DASHBOARD_PATH = '/agent/dashboard'
const TERRACE_LENDER_DASHBOARD_PATH = '/lender/dashboard'
const TERRACE_LENDER_GET_CLIENT_STARTED_PATH = '/lender/get-started'
const TERRACE_AGENT_GET_CLIENT_STARTED_PATH = '/agent/get-started'
const TERRACE_SET_LEAD_DATA_PATH = '/api/set-lead-data'
const TERRACE_PROFILE_PATH = '/profile'
// Logging out from the signup page returns the user to that same page, so
// someone signed in as the wrong account can immediately create the account
// they came for instead of dead-ending elsewhere.
const LOGOUT_WITH_REDIRECT_TO_TERRACE_SIGNUP = `${authApiPaths.LOGOUT}?returnTo=${encodeURIComponent(APP_BASE_URL + TERRACE_AUTH_SIGNUP_PATH)}`

// LAYOUT/STYLING
const HEADER_HEIGHT_DESKTOP = 76
const HEADER_HEIGHT_MOBILE = 60
const SIDE_NAV_WIDTH_DESKTOP = 100
const SIDE_NAV_WIDTH_MOBILE = 275
const MOBILE_BREAKPOINT = '48em' // This matches the mobile breakpoint defined in the mantinev7 theme - default-theme

// Navigation
const FIRST_VISIT_QUERY_PARAM = 'welcome'
const LEAD_SHARED_QUERY_PARAM = 'shared'
const TERRACE_HOME_DASHBOARD_WELCOME_PATH = `${TERRACE_HOME_DASHBOARD_PATH}?${FIRST_VISIT_QUERY_PARAM}=true`

const CONTRACT_FEE_PROMOTIONS = {
  v1: {
    startDate: '2025-10-01',
    endDate: '2025-12-31',
    fundedByDate: '2026-02-27',
    capAmount: 29500,
  },
}

const ENABLE_BRIDGE_LOAN_PLUS = process.env.ENABLE_BRIDGE_LOAN_PLUS === 'true'
const DISPLAY_ORIGINATION_FEE = process.env.DISPLAY_ORIGINATION_FEE === 'true'

const ENABLE_ORIGINATION_FEE_PROMO = process.env.ENABLE_ORIGINATION_FEE_PROMO === 'true'
const ORIGINATION_FEE_PROMO_CONFIG = {
  percent: process.env.ORIGINATION_FEE_PROMO_PERCENT ?? '',
  startDate: process.env.ORIGINATION_FEE_PROMO_START_DATE ?? '',
  endDate: process.env.ORIGINATION_FEE_PROMO_END_DATE ?? '',
  fundDate: process.env.ORIGINATION_FEE_PROMO_FUND_DATE ?? '',
}

export {
  APP_BASE_URL,
  APP_ENV,
  APP_IS_LOCAL,
  APP_IS_PROD,
  APP_NAME,
  APP_REV,
  APPZI_TOKEN,
  AUTH0_AUDIENCE,
  AUTH0_ISSUER_BASE_URL,
  authApiPaths,
  COOKIE_POLICY_URL,
  COOKIE_YES_CONSENT_COOKIE,
  COOKIE_YES_SCRIPT_ID,
  CONTACT_US_URL,
  COURTYARD_URL,
  DD_APPLICATION_ID,
  DD_CLIENT_TOKEN,
  DD_ENABLED,
  DISABLE_ALL_ANALYTICS,
  DO_NOT_SELL_INFO_URL,
  FAQ_URL,
  FIRST_VISIT_QUERY_PARAM,
  FRONTYARD_URL,
  GQL_PROXY_URI,
  GQL_PUBLIC_URI,
  GQL_URI,
  GTAG_ID,
  HEADER_HEIGHT_DESKTOP,
  HEADER_HEIGHT_MOBILE,
  HEAP_PROXY,
  HEAP_TOKEN,
  HOMEBUYER_SIGNUP_URL,
  KNOCK_PRIVACY_COOKIE,
  KNOCK_RETURNING_USER_COOKIE,
  KNOCK_SSO_PROBED_COOKIE,
  LEAD_SHARED_QUERY_PARAM,
  LIVE_WEBINAR_SLUG,
  LIVE_WEBINARS_URL,
  LOGOUT_WITH_REDIRECT_TO_TERRACE_SIGNUP,
  MOBILE_BREAKPOINT,
  PRIVACY_POLICY_URL,
  PUBLIC_AUTH0_CLIENT_ID,
  PUBLIC_AUTH0_ISSUER_BASE_URL,
  QUALIFY_URL,
  RESOURCE_CENTER,
  SIDE_NAV_WIDTH_DESKTOP,
  SIDE_NAV_WIDTH_MOBILE,
  STORYBLOK_CDN_TOKEN,
  STORYBLOK_VERSION,
  TERMS_OF_USE_URL,
  TERRACE_AGENT_DASHBOARD_PATH,
  TERRACE_AGENT_GET_CLIENT_STARTED_PATH,
  TERRACE_AUTH_LOGIN_PATH,
  TERRACE_AUTH_LOGOUT_PATH,
  TERRACE_AUTH_SIGNUP_PATH,
  TERRACE_AUTH_SIGNUP_STEP2_PATH,
  TERRACE_EMAIL_VERIFY_PATH,
  TERRACE_HOME_DASHBOARD_PATH,
  TERRACE_HOME_DASHBOARD_WELCOME_PATH,
  TERRACE_LENDER_DASHBOARD_PATH,
  TERRACE_LENDER_GET_CLIENT_STARTED_PATH,
  TERRACE_PROFILE_PATH,
  TERRACE_SET_LEAD_DATA_PATH,
  UNIVERSAL_LEAD_COOKIE,
  CONTRACT_FEE_PROMOTIONS,
  ENABLE_BRIDGE_LOAN_PLUS,
  DISPLAY_ORIGINATION_FEE,
  ENABLE_ORIGINATION_FEE_PROMO,
  ORIGINATION_FEE_PROMO_CONFIG,
}
